/* eslint-disable react/prop-types */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createStructuredSelector } from 'reselect';
import Loadable from 'react-loadable';
// Actions:
import {
    addToCartSuccess as addToCartSuccessAction,
    removeOrderLineItem as removeOrderLineItemAction,
    changeOrderLineItem as changeOrderLineItemAction,
    removeFromCartSuccess as removeFromCartSuccessAction,
} from 'ggApp/modules/order/actions';

import { deleteOrder } from 'ggApp/modules/order/storage';
import { pushDataLayer } from 'ggApp/modules/tracking/actions';
// Selectors:
import { cartOrderProductTracking as cartOrderProductTrackingSelector } from 'ggApp/modules/tracking/selectors';

import { criteoCartTracking as criteoCartTrackingSelector } from 'ggApp/modules/criteo/selectors';

import {
    orderActive as activeOrderSelector,
    fetching as isOrderFetchingSelector,
    unavailableSkuList as unavailableSkuListSelector,
} from 'ggApp/modules/order/selectors';

import { user as userSelector } from 'ggApp/modules/auth/selectors';
import { storeActive as activeStoreSelector } from 'ggApp/modules/store/selectors';
// Components
import { Button } from '@getgrover/ui';

import {
    OrderComponentTable as OrderTable,
    OrderComponentTableMobile as OrderTableMobile,
} from 'ggApp/modules/order';
import { UnavailableBannerContext } from 'ggApp/modules/order/components/UnavailableBannerContex';

import DisplayIf from 'ggApp/shared/components/viewport';

import {
    LayoutComponent_Text as Text,
    LayoutComponent_Container as Container,
    LayoutComponent_Checkout_Title as CheckoutTitle,
} from 'ggApp/modules/layout';

import { TranslatorComponent_Text as Translator } from 'ggApp/modules/translator';

import BackToHomeLink from './BackToHomeLink';
import ProceedButton from './ProceedButton';
// Components end;

const UnavailableBanner = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "UnavailableBanner" */ 'ggApp/shared/screens/Cart/UnavailableBanner'
        ),
    loading: () => null,
    delay: 300,
    timeout: 10000,
});

@connect(
    createStructuredSelector({
        user: userSelector,
        order: activeOrderSelector,
        activeStore: activeStoreSelector,
        orderFetching: isOrderFetchingSelector,
        criteoCartTracking: criteoCartTrackingSelector,
        cartOrderProductTracking: cartOrderProductTrackingSelector,
        unavailableSkuList: unavailableSkuListSelector,
    }),
    (dispatch) => ({
        addToCartSuccess: bindActionCreators(addToCartSuccessAction, dispatch),
        changeOrderLineItem: bindActionCreators(changeOrderLineItemAction, dispatch),
        removeOrderLineItem: bindActionCreators(removeOrderLineItemAction, dispatch),
        removeFromCartSuccess: bindActionCreators(removeFromCartSuccessAction, dispatch),
    }),
)
export default class CartScreen extends PureComponent {
    componentDidMount() {
        const { criteoCartTracking, cartOrderProductTracking } = this.props;

        pushDataLayer(cartOrderProductTracking);
        pushDataLayer(criteoCartTracking);
    }

    changeItemQuantity = (product, newQuantity) => {
        const { order, addToCartSuccess, changeOrderLineItem, removeFromCartSuccess } = this.props;

        changeOrderLineItem(order.number, product.id, newQuantity).then(() => {
            if (product.quantity < newQuantity) {
                addToCartSuccess({ productId: product.id });
            }

            if (product.quantity > newQuantity) {
                removeFromCartSuccess({
                    product,
                    quantity: newQuantity,
                    orderNumber: order.number,
                });
            }
        });
    };

    removeItem = (product) => {
        const { order, activeStore, removeOrderLineItem, removeFromCartSuccess } = this.props;

        const isLastLineItem =
            order.items.length === 1 && order.items.filter((i) => i.id === product.id).length > 0;

        removeOrderLineItem(order.number, product.id).then(() => {
            removeFromCartSuccess({ product, quantity: 0, orderNumber: order.number });
        });

        if (isLastLineItem) {
            deleteOrder(activeStore.code);
        }
    };

    renderProceedButton = (options = { primary: true }) => {
        const { primary } = options;
        const { orderFetching, unavailableSkuList } = this.props;

        return (
            <ProceedButton
                component={Button}
                loading={orderFetching}
                primary={primary}
                disabled={unavailableSkuList && unavailableSkuList.length !== 0}
                centred
                fullWidth
            >
                <Translator tk="CART_NEXT_BUTTON" />
            </ProceedButton>
        );
    };

    renderDesktopView = () => {
        const { order, orderFetching, unavailableSkuList } = this.props;
        const singleProductUnavailable = unavailableSkuList && unavailableSkuList.length === 1;
        return (
            <section>
                <CheckoutTitle tk="CART_HOME_TITLE" />

                <Container>
                    {/* Pass UnavailableBanner component using React context to avoid external dependencies import for table components */}
                    <UnavailableBannerContext.Provider value={UnavailableBanner}>
                        <OrderTable
                            order={order}
                            changeItemQuantity={this.changeItemQuantity}
                            removeItem={this.removeItem}
                            buttonLoading={orderFetching}
                            summaryNote={false}
                            renderButton={this.renderProceedButton}
                        >
                            {// TODO: remove layout hack in future
                            singleProductUnavailable && <div style={{ minHeight: 348 }} />}
                        </OrderTable>
                    </UnavailableBannerContext.Provider>
                    <Text>
                        <BackToHomeLink />
                    </Text>
                </Container>
            </section>
        );
    };

    renderMobileView = () => {
        const { order } = this.props;

        return (
            <section>
                <CheckoutTitle tk="CART_HOME_TITLE" />
                {/* Pass UnavailableBanner component using React context to avoid external dependencies import for table components */}
                <UnavailableBannerContext.Provider value={UnavailableBanner}>
                    <OrderTableMobile
                        order={order}
                        changeItemQuantity={this.changeItemQuantity}
                        removeItem={this.removeItem}
                        displaySummaryNote={false}
                    >
                        <Container>
                            <Text center>
                                <BackToHomeLink />
                            </Text>
                        </Container>
                    </OrderTableMobile>
                </UnavailableBannerContext.Provider>
                <Container>{this.renderProceedButton({ primary: true })}</Container>
            </section>
        );
    };

    render() {
        return (
            <section>
                <DisplayIf desktop largeDesktop widescreen>
                    {this.renderDesktopView()}
                </DisplayIf>

                <DisplayIf mobile tablet>
                    {this.renderMobileView()}
                </DisplayIf>
            </section>
        );
    }
}

CartScreen.childContextTypes = { UnavailableBanner: PropTypes.func };

CartScreen.propTypes = {
    order: PropTypes.shape({}).isRequired,
    orderFetching: PropTypes.bool.isRequired,
    activeStore: PropTypes.shape({}).isRequired,
    criteoCartTracking: PropTypes.shape({}).isRequired,
    cartOrderProductTracking: PropTypes.shape({}).isRequired,
    addToCartSuccess: PropTypes.func.isRequired,
    changeOrderLineItem: PropTypes.func.isRequired,
    removeOrderLineItem: PropTypes.func.isRequired,
    removeFromCartSuccess: PropTypes.func.isRequired,
};
